import type { StaticDataRouteOption } from "@tanstack/react-router";
import type {
  CopilotRouteMetadata,
  CopilotToolset,
} from "@/features/copilot/lib/copilot-page-context";

type RouteParams = CopilotRouteMetadata["params"];

function page(
  toolset: CopilotToolset,
  label: string,
  starters: readonly string[],
  params?: RouteParams,
  searchParams?: CopilotRouteMetadata["searchParams"],
): CopilotRouteMetadata {
  return { toolset, label, starters, params, searchParams };
}

const ATTENDANCE_STARTERS = [
  "Summarize my attendance this month",
  "Which days was I late or absent?",
  "Do I have any attendance requests waiting?",
];
const ATTENDANCE_TEAM_STARTERS = [
  "Who on my team is absent today?",
  "Which attendance requests need my decision?",
  "Show late arrivals on my team this week",
];
const LEAVE_STARTERS = [
  "What is my leave balance?",
  "Show my upcoming leave",
  "Which leave requests are still pending?",
];
const LEAVE_TEAM_STARTERS = [
  "Who on my team is on leave this week?",
  "Which leave requests need my decision?",
  "Are there overlapping leave dates on my team?",
];
const LEAVE_SETUP_STARTERS = [
  "What is left to set up for leave?",
  "Which leave types are not used by any plan?",
  "Who has no leave plan yet?",
];
const ATTENDANCE_SETUP_STARTERS = [
  "What is left to set up for attendance?",
  "Who has no shift today?",
  "Does my holiday calendar cover the next month?",
];
const GOALS_STARTERS = [
  "Which of my goals are at risk?",
  "Summarize progress on my goals",
  "What goals have no recent check-in?",
];
const GOAL_DETAIL_STARTERS = [
  "Summarize this goal",
  "What is blocking this goal?",
  "Show the recent check-ins on this goal",
];
const TODOS_STARTERS = [
  "What is due this week?",
  "Show my overdue tasks",
  "Summarize my open tasks",
];
const TODO_DETAIL_STARTERS = [
  "Summarize this task",
  "When is this task due?",
  "Who else is involved in this task?",
];
const KUDOS_STARTERS = [
  "Who recognized me recently?",
  "Show recent kudos across the workspace",
  "Who have I recognized lately?",
];
const KUDOS_DETAIL_STARTERS = ["Summarize this kudos", "Who gave and received this kudos?"];
const FEEDBACK_STARTERS = [
  "Summarize the feedback I received recently",
  "Do I have feedback requests waiting?",
  "Show feedback I have given",
];
const FEEDBACK_DETAIL_STARTERS = [
  "Summarize this feedback",
  "What themes come up in this feedback?",
];
const PEOPLE_STARTERS = [
  "Who works in this workspace?",
  "Who joined recently?",
  "Find people in a department",
];
const PERSON_STARTERS = [
  "Summarize this person's role and team",
  "Who does this person report to?",
  "Who reports to this person?",
];
const ORG_CHART_STARTERS = [
  "Who are the managers in this workspace?",
  "How is the organization structured?",
];
const MY_TEAM_STARTERS = [
  "Who is on my team?",
  "Which of my reports need attention?",
  "Summarize my team's open goals",
];
const PULSE_STARTERS = [
  "Which pulse surveys are open?",
  "Summarize recent pulse results",
  "Which actions are overdue?",
];
const PULSE_SURVEY_STARTERS = [
  "Summarize this survey",
  "What is the response rate?",
  "Show the main themes in the answers",
];
const PULSE_ACTION_STARTERS = [
  "Summarize this action",
  "Is this action on track?",
  "What measurement is attached to this action?",
];
const REVIEWS_STARTERS = [
  "Which review cycles are active?",
  "What reviews are waiting on me?",
  "Summarize review progress",
];
const REVIEW_CYCLE_STARTERS = [
  "Summarize this review cycle",
  "Who has not finished their review?",
  "What is the deadline for this cycle?",
];
const REVIEW_TEMPLATE_STARTERS = [
  "Summarize this review template",
  "Which cycles use this template?",
];

// Wire names follow the arguments of pulse.insights.list and pulse.actions.list, so the model can
// pass them straight through. The tab and time window are left out: no pulse read accepts one.
const INSIGHT_SEARCH_PARAMS = {
  audience: "insightAudience",
  status: "insightStatus",
  urgency: "insightSeverity",
  insightId: "insightId",
} as const;
const ACTION_SEARCH_PARAMS = { status: "status" } as const;

const reviewCycleParticipantParams: RouteParams = ["cycleId", "participantId"];

/**
 * Every route that offers page context to Copilot, keyed by route path. Routes
 * absent here (payroll, compensation, expenses, recruitment, other settings,
 * one-on-ones, home, dashboards, people edit and HR) send nothing.
 */
export const COPILOT_ROUTE_CATALOG = {
  "/attendance": page("attendance", "Attendance", ATTENDANCE_STARTERS),
  "/attendance/team": page("attendance", "Team attendance", ATTENDANCE_TEAM_STARTERS),
  "/leave": page("leave", "Leave", LEAVE_STARTERS),
  "/leave/balances": page("leave", "Leave balances", LEAVE_STARTERS),
  "/leave/history": page("leave", "Leave history", LEAVE_STARTERS),
  "/leave/team": page("leave", "Team leave", LEAVE_TEAM_STARTERS),
  "/settings/leave": page("leave_setup", "Leave settings", LEAVE_SETUP_STARTERS),
  "/settings/attendance": page(
    "attendance_setup",
    "Attendance settings",
    ATTENDANCE_SETUP_STARTERS,
  ),
  "/goals": page("goals", "Goals", GOALS_STARTERS),
  "/goals/$goalId": page("goals", "Goal", GOAL_DETAIL_STARTERS, ["goalId"]),
  "/todos": page("todos", "Tasks", TODOS_STARTERS),
  "/todos/$todoId": page("todos", "Task", TODO_DETAIL_STARTERS, ["todoId"]),
  "/kudos": page("kudos", "Kudos", KUDOS_STARTERS),
  "/kudos/$kudosId": page("kudos", "Kudos", KUDOS_DETAIL_STARTERS, ["kudosId"]),
  "/feedback": page("feedback", "Feedback", FEEDBACK_STARTERS),
  "/feedback/$feedbackId": page("feedback", "Feedback", FEEDBACK_DETAIL_STARTERS, ["feedbackId"]),
  "/people": page("people", "People", PEOPLE_STARTERS),
  "/people/$membershipId": page("people", "Person", PERSON_STARTERS, ["membershipId"]),
  "/org-chart": page("people", "Org chart", ORG_CHART_STARTERS),
  "/my-team": page("people", "My team", MY_TEAM_STARTERS),
  "/my-pulse": page("pulse", "My pulse", PULSE_STARTERS),
  "/pulse-surveys": page("pulse", "Pulse surveys", PULSE_STARTERS),
  "/pulse-surveys/$surveyId": page("pulse", "Pulse survey", PULSE_SURVEY_STARTERS, ["surveyId"]),
  "/insights": page("pulse", "Pulse insights", PULSE_STARTERS, undefined, INSIGHT_SEARCH_PARAMS),
  "/actions": page("pulse", "Pulse actions", PULSE_STARTERS, undefined, ACTION_SEARCH_PARAMS),
  "/actions/$actionId": page("pulse", "Pulse action", PULSE_ACTION_STARTERS, ["actionId"]),
  "/performance-reviews": page("reviews", "Performance reviews", REVIEWS_STARTERS),
  "/performance-reviews/cycles/$cycleId": page("reviews", "Review cycle", REVIEW_CYCLE_STARTERS, [
    "cycleId",
  ]),
  "/performance-reviews/cycles/$cycleId/manager-ratings": page(
    "reviews",
    "Manager ratings",
    REVIEW_CYCLE_STARTERS,
    ["cycleId"],
  ),
  "/performance-reviews/cycles/$cycleId/self-review": page(
    "reviews",
    "Self review",
    REVIEW_CYCLE_STARTERS,
    ["cycleId"],
  ),
  "/performance-reviews/cycles/$cycleId/participants/$participantId/manager-review": page(
    "reviews",
    "Manager review",
    REVIEW_CYCLE_STARTERS,
    reviewCycleParticipantParams,
  ),
  "/performance-reviews/cycles/$cycleId/participants/$participantId/results": page(
    "reviews",
    "Review results",
    REVIEW_CYCLE_STARTERS,
    reviewCycleParticipantParams,
  ),
  "/performance-reviews/cycles/$cycleId/participants/$participantId/peer-review": page(
    "reviews",
    "Peer review",
    REVIEW_CYCLE_STARTERS,
    reviewCycleParticipantParams,
  ),
  "/performance-reviews/templates/$templateId": page(
    "reviews",
    "Review template",
    REVIEW_TEMPLATE_STARTERS,
    ["templateId"],
  ),
} as const satisfies Record<string, CopilotRouteMetadata>;

export type CopilotCoveredPath = keyof typeof COPILOT_ROUTE_CATALOG;

/** Route option spread for a covered route, so the router never restates labels. */
export function copilotStaticData(path: CopilotCoveredPath): StaticDataRouteOption {
  return { copilot: COPILOT_ROUTE_CATALOG[path] };
}
